// Receiving on an order (supplier hub, PR 3 — 16 Oct 2026). Until this panel, `receive_delivery` had no screen: of 13
// live AIW orders, 9 said "sent" and none was ever received, so stock never arrived on the books and no supplier had an
// on-time figure. One row per ordered line: what has arrived, what arrives now, and — for a stock line — which shelf.
// A line for a Job goes to the Job (no stock movement); a stock line adds on hand. The order closes itself when every
// line is fully received; anything less is a part delivery and the order stays open.
import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {sydneyToday, sydneyWallTimeToIso} from '@artemis-group/aiw-contracts';
import {Button} from '@aiw/ui';
import {Panel} from '../shell/Page';
import {useT} from '../lib/i18n';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useOfficeAction} from '../hooks/useOfficeAction';
import {DataTable, Td} from './aligned-table';
import {receiptOf, type PlanLine} from '../lib/delivery-receipt';

type Plan = {po: {id: string; status: string}; lines: PlanLine[]; receipts: {id: string; po_line_no: number; qty: number; standing_qty: number; location: string; delivered_at: string; recorded_by: string; note: string}[]};
type Location = {code: string; label: string; kind: string};

async function get<T>(path: string): Promise<T> {
  const r = await fetchImpl(`${env.apiBase}${path}`, {headers: await authHeaders()});
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Unavailable');
  return body as T;
}

const n3 = (n: number) => n.toLocaleString('en-AU', {maximumFractionDigits: 3});

export function ReceiveDelivery({poId, status}: {poId: string; status: string}) {
  const {t} = useT();
  const action = useOfficeAction();
  const open = ['sent', 'acknowledged'].includes(status.toLowerCase());
  const plan = useQuery({queryKey: ['delivery-plan', poId], queryFn: () => get<{data: Plan}>(`/api/purchase-orders/${poId}/delivery-plan`).then((r) => r.data)});
  const locations = useQuery({queryKey: ['inventory-locations'], queryFn: () => get<{locations: Location[]}>('/api/inventory').then((r) => r.locations), enabled: open});
  const [arriving, setArriving] = useState<Record<number, string>>({});
  const [where, setWhere] = useState<Record<number, string>>({});
  const [on, setOn] = useState(sydneyToday());
  const [note, setNote] = useState('');
  const p = plan.data;
  if (plan.isError) return <Panel title={t('po.receive.title')}><p role="alert" className="text-sm text-warning">{(plan.error as Error).message}</p></Panel>;
  if (!p) return null;
  const pending = p.lines.filter((l) => l.remaining_qty > 0);
  const r = receiptOf(p.lines, arriving, where);
  const fillAll = () => setArriving(Object.fromEntries(pending.map((l) => [l.line_no, String(l.remaining_qty)])));
  const submit = async () => {
    const delivered = sydneyWallTimeToIso(`${on}T12:00`);
    const res = await action.run('receive_delivery', {po_id: poId, lines: r.lines, delivered_at: delivered, complete: r.complete, note});
    if (res?.status === 'executed') { setArriving({}); setNote(''); await plan.refetch(); }
  };

  return <Panel title={t('po.receive.title')} meta={open ? (pending.length ? t('po.receive.pending', {count: pending.length}) : t('po.receive.allIn')) : t('po.receive.closed')}>
    <DataTable density="compact" cols={[
      {label: t('po.line.item')}, {label: t('po.line.ordered'), width: 90, num: true}, {label: t('po.receive.received'), width: 90, num: true},
      ...(open ? [{label: t('po.receive.now'), width: 110, num: true}, {label: t('po.receive.where'), width: 160}] : []),
    ]}>
      {p.lines.map((l) => (
        <tr key={l.line_no}>
          <Td title={l.description}>{l.description}</Td>
          <Td num>{n3(l.qty)} <span className="font-normal text-muted-foreground">{l.unit}</span></Td>
          <Td num className={l.remaining_qty === 0 ? 'text-success' : undefined}>{n3(l.received_qty)}</Td>
          {open && <Td num>{l.remaining_qty > 0
            ? <input aria-label={t('po.receive.nowFor', {item: l.description})} type="number" min="0" step="any" max={l.remaining_qty}
                className="h-7 w-24 rounded border bg-background px-1.5 text-right text-sm tabular-nums" value={arriving[l.line_no] ?? ''} placeholder="0"
                onChange={(e) => setArriving((a) => ({...a, [l.line_no]: e.target.value}))}/>
            : <span className="font-normal text-muted-foreground">—</span>}</Td>}
          {open && <Td>{l.to_inventory
            ? <select aria-label={t('po.receive.where')} className="h-7 w-full rounded border bg-background px-1 text-xs" value={where[l.line_no] ?? 'General Storage'} onChange={(e) => setWhere((w) => ({...w, [l.line_no]: e.target.value}))}>
                {(locations.data ?? [{code: 'General Storage', label: 'General Storage', kind: 'general'}]).filter((x) => x.code !== 'warehouse').map((x) => <option key={x.code} value={x.code}>{x.label}</option>)}
              </select>
            : <span className="text-xs text-muted-foreground">{t('po.receive.toJob', {job: l.for_job_ref ?? '—'})}</span>}</Td>}
        </tr>
      ))}
    </DataTable>

    {open && pending.length > 0 && <div className="mt-3 grid gap-2">
      <div className="flex flex-wrap items-end gap-3">
        <button type="button" className="text-sm text-primary underline" onClick={fillAll}>{t('po.receive.everything')}</button>
        <label className="grid gap-0.5 text-xs text-muted-foreground">{t('po.receive.on')}
          <input type="date" className="h-8 rounded-md border bg-background px-2 text-sm text-foreground" value={on} max={sydneyToday()} onChange={(e) => setOn(e.target.value)}/>
        </label>
        <label className="grid min-w-48 flex-1 gap-0.5 text-xs text-muted-foreground">{t('po.receive.note')}
          <input className="h-8 rounded-md border bg-background px-2 text-sm text-foreground" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder={t('po.receive.notePlaceholder')}/>
        </label>
      </div>
      <p className={r.over ? 'text-sm text-warning' : 'text-sm text-muted-foreground'}>
        {r.over ? t('po.receive.over') : r.lines.length === 0 ? t('po.receive.nothing') : r.complete ? t('po.receive.closes') : t('po.receive.part')}
      </p>
      <div><Button disabled={action.busy || r.over || r.lines.length === 0 || !on} onClick={() => void submit()}>{t('po.receive.record')}</Button></div>
      {action.notice && <p role="status" className="text-sm">{action.notice}</p>}
    </div>}

    {p.receipts.length > 0 && <details className="mt-3 text-sm"><summary>{t('po.receive.history', {count: p.receipts.length})}</summary>
      {p.receipts.map((x) => <div key={x.id} className="mt-1.5 border-t pt-1.5 text-xs">
        {t('po.receive.line', {line: x.po_line_no})} · {n3(x.standing_qty)}{x.standing_qty !== x.qty ? ` (${n3(x.qty)})` : ''} · {x.location} · {new Date(x.delivered_at).toLocaleDateString('en-AU', {day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney'})} · {x.recorded_by}{x.note ? ` · ${x.note}` : ''}
      </div>)}
    </details>}
  </Panel>;
}
